import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UploadCloud, Download, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";

export default function AdminImportPage() {
  const [entities, setEntities] = useState([]);
  const [entity, setEntity] = useState("categories");
  const [file, setFile] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [committing, setCommitting] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(false);

  useEffect(() => {
    api.get("/admin/import/entities").then((r) => setEntities(r.data || []));
    api.get("/admin/import/history").then((r) => setHistory(r.data || []));
  }, []);

  const downloadTemplate = () => {
    const token = window.localStorage.getItem("gcw_bearer") || "";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/import/${entity}/template.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${entity}-template.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      });
  };

  const upload = async () => {
    if (!file) { toast.error("Choose a CSV first"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/admin/import/${entity}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setJob(data);
      toast.success(`Parsed: ${data.valid_rows} valid, ${data.error_rows} error rows`);
    } catch (err) { toast.error(err?.response?.data?.error || "Upload failed"); }
  };

  const commit = async () => {
    if (!job?.id) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/admin/import/commit", { job_id: job.id, update_existing: updateExisting });
      toast.success(`Imported ${data.committed} row(s)`);
      setJob(null);
      setFile(null);
      const h = await api.get("/admin/import/history");
      setHistory(h.data || []);
    } catch (err) { toast.error(err?.response?.data?.error || "Commit failed"); } finally { setCommitting(false); }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-import">
      <div className="mb-6">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Import</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">CSV import</h1>
        <p className="text-[#252A34]/70 text-sm mt-1">Two-step: preview → validate → commit. Max 5MB.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <select value={entity} onChange={(e) => setEntity(e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" data-testid="import-entity">
            {entities.map((e) => <option key={e.key} value={e.key}>{e.key}</option>)}
          </select>
          <button onClick={downloadTemplate} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="import-template">
            <Download className="h-4 w-4" /> Template
          </button>
          <input type="file" accept=".csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="import-file" />
        </div>
        <div className="text-xs text-[#252A34]/60">Required: {(entities.find((x) => x.key === entity)?.required || []).join(", ")}. Optional: {(entities.find((x) => x.key === entity)?.optional || []).join(", ")}.</div>
        <button onClick={upload} disabled={!file} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium disabled:opacity-50" data-testid="import-upload">
          <UploadCloud className="h-4 w-4" /> Upload & preview
        </button>
      </div>

      {job && (
        <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="import-preview">
          <div className="flex items-center gap-3">
            <div className="font-display font-semibold text-[#071b22]">Preview: {job.entity}</div>
            <div className="text-xs text-[#252A34]/60">total {job.total_rows} · valid {job.valid_rows} · errors {job.error_rows}</div>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60 mb-1">Sample rows</div>
              <ul className="rounded-xl border border-slate-200 max-h-64 overflow-auto divide-y">
                {(job.preview_sample || []).map((s, i) => (
                  <li key={i} className={`p-3 text-xs ${s.errors?.length ? "bg-rose-50" : "bg-white"}`}>
                    {s.errors?.length ? <AlertCircle className="h-3 w-3 inline text-rose-600" /> : <CheckCircle2 className="h-3 w-3 inline text-emerald-600" />}
                    <span className="ml-1 font-mono">row {s.row}</span>
                    {s.errors?.length > 0 && <div className="text-rose-700 mt-1">{s.errors.join(", ")}</div>}
                    <div className="text-[#252A34]/60 mt-1 truncate">{JSON.stringify(s.data)}</div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60 mb-1">Errors</div>
              <ul className="rounded-xl border border-slate-200 max-h-64 overflow-auto divide-y">
                {(job.errors || []).length === 0 ? (
                  <li className="p-3 text-xs text-emerald-700">No validation errors</li>
                ) : (job.errors || []).map((e, i) => (
                  <li key={i} className="p-3 text-xs bg-rose-50">
                    row {e.row} — {e.errors.join(", ")}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-xs text-[#252A34]">
              <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} data-testid="import-update-existing" /> Update existing rows (by unique keys)
            </label>
            <button onClick={commit} disabled={committing || job.valid_rows === 0} className="ml-auto inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2 text-sm font-medium disabled:opacity-50" data-testid="import-commit">
              {committing ? "Committing…" : `Import ${job.valid_rows} valid row(s)`}
            </button>
          </div>
        </div>
      )}

      <div className="mt-8">
        <h2 className="font-display text-lg font-semibold text-[#071b22] mb-3">History</h2>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="import-history">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#252A34]/60 bg-slate-50">
                <th className="px-3 py-2">When</th><th className="px-3 py-2">Entity</th><th className="px-3 py-2">Filename</th><th className="px-3 py-2">Rows</th><th className="px-3 py-2">Committed</th><th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={6} className="p-6 text-center text-[#252A34]/60">No imports yet.</td></tr>
              ) : history.map((h) => (
                <tr key={h.id} className="border-t border-slate-100 text-xs" data-testid={`import-history-${h.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{new Date(h.created_at).toLocaleString()}</td>
                  <td className="px-3 py-2">{h.entity}</td>
                  <td className="px-3 py-2">{h.filename}</td>
                  <td className="px-3 py-2">{h.total_rows} <span className="text-[#252A34]/60">({h.valid_rows} valid)</span></td>
                  <td className="px-3 py-2">{h.committed_rows}</td>
                  <td className="px-3 py-2">{h.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
